import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { pulse } from './LightField'

/**
 * A vertical list of answer rows. Keys 1–9 select; the chosen row shows its
 * selected state for a beat (and sends a pulse into the light field) before
 * `onChoose` fires, so the tap feels acknowledged.
 */
function AnswerRows({ options, onChoose, value = null, keyboard = true, autoAdvance = true }) {
  const [pending, setPending] = useState(null)

  useEffect(() => {
    setPending(null)
  }, [options])

  const choose = (index, el) => {
    if (pending !== null) return
    if (el) {
      const r = el.getBoundingClientRect()
      pulse(0.7, { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight })
      // Drop focus from the tapped row so nothing carries a ring into the next screen.
      el.blur()
    } else {
      pulse(0.5)
    }
    if (!autoAdvance) {
      onChoose(index)
      return
    }
    setPending(index)
    window.setTimeout(() => onChoose(index), 220)
  }

  useEffect(() => {
    if (!keyboard) return undefined
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      const n = Number(e.key)
      if (Number.isInteger(n) && n >= 1 && n <= options.length) {
        e.preventDefault()
        choose(n - 1, document.querySelectorAll('.row')[n - 1])
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, pending, keyboard])

  const selected = pending !== null ? pending : value

  return (
    <div className="rows" role="group">
      {options.map((opt, i) => (
        <button
          key={opt.key ?? i}
          type="button"
          className="row"
          aria-pressed={selected === i}
          onClick={(e) => choose(i, e.currentTarget)}
        >
          <span className="row__key" aria-hidden="true">{i + 1}</span>
          <span className="row__text">
            <span className="row__title">{opt.title}</span>
            {opt.sub && <span className="row__sub">{opt.sub}</span>}
          </span>
          <span />
        </button>
      ))}
    </div>
  )
}

AnswerRows.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.node.isRequired,
    sub: PropTypes.node,
  })).isRequired,
  onChoose: PropTypes.func.isRequired,
  value: PropTypes.number,
  keyboard: PropTypes.bool,
  autoAdvance: PropTypes.bool,
}

export default AnswerRows
